/**
 * Une section du dock découpée en ONGLETS (ROADMAP 3.1, demande de Benoit du 20 août :
 * « fais des onglets aussi dans les autres menus comme dans construire »).
 *
 * ══ Pourquoi ce fichier, et pas des onglets écrits dans chaque section ═══════
 *
 * La boutique s'était fabriqué les siens (`shop-tabs`) alors que la bibliothèque de
 * composants en proposait déjà (`createTabs`, `v-tabs`). Deux systèmes pour une même
 * idée, c'est deux apparences qui divergent au premier ajustement, et deux endroits où
 * corriger un défaut de cible tactile.
 *
 * Ce module est la troisième pièce qui manquait : le composant `createTabs` dit quel
 * onglet est actif, celui-ci décide **ce qui s'affiche**. Une section n'a donc plus à
 * savoir gérer une bascule — elle donne ses panneaux et leurs libellés.
 *
 * ══ Ce qu'il ne fait PAS ═════════════════════════════════════════════════════
 *
 * Il ne démonte rien : les panneaux inactifs sont **cachés**, pas détruits. Leur
 * `update()` continue donc de tourner, et un panneau retrouvé après une bascule est déjà
 * à jour au lieu de se peindre sous les yeux du joueur. C'est aussi ce qui permet à une
 * pastille de notification d'apparaître sur un onglet qu'on ne regarde pas.
 */

import type { Component } from './components';
import { createTabs } from './components';
import type { IconName } from './icons';

export interface TabbedEntry {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName;
  /** Panneaux montrés sous cet onglet, dans l'ordre. */
  readonly content: readonly HTMLElement[];
  /**
   * Appelé quand cet onglet DEVIENT actif, jamais tant qu'il ne l'est pas.
   *
   * ⚠️ C'est la seule façon correcte de charger le contenu d'un onglet qui interroge le
   * réseau. Sans lui, il ne restait que deux mauvaises options : charger les quatre
   * onglets au montage — donc payer une requête pour trois panneaux que le joueur ne
   * regardera peut-être jamais — ou rafraîchir en boucle depuis le tick de jeu, ce qui
   * ferait battre une liste qui ne bouge que quelques fois par jour.
   */
  readonly onShow?: () => void;
}

export interface TabbedSectionHandle extends Component {
  /** Bascule sur un onglet — utile pour emmener le joueur là où l'action se fait. */
  select(id: string): void;
  /** Pastille sur un onglet — relayée telle quelle au composant. */
  setBadge(id: string, count: number): void;
  getActive(): string;
}

export function createTabbedSection(entries: readonly TabbedEntry[]): TabbedSectionHandle {
  const el = document.createElement('div');
  el.className = 'tabbed-section';

  const paint = (active: string): void => {
    for (const entry of entries) {
      for (const node of entry.content) node.hidden = entry.id !== active;
    }
    /*
      Le crochet part APRÈS l'affichage, pas avant : un panneau qui se remplit alors qu'il
      est encore `hidden` ne peut pas mesurer sa propre mise en page, et une animation
      d'entrée jouerait dans le vide.
    */
    entries.find((entry) => entry.id === active)?.onShow?.();
  };

  const tabs = createTabs({
    tabs: entries.map((entry) => ({
      id: entry.id,
      label: entry.label,
      ...(entry.icon === undefined ? {} : { icon: entry.icon }),
    })),
    onChange: paint,
  });

  const body = document.createElement('div');
  body.className = 'tabbed-section__body';
  for (const entry of entries) body.append(...entry.content);

  /*
    ── L'EN-TÊTE COLLANT, DERRIÈRE LES ONGLETS ET NON SUR EUX ────────────────

    « Les onglets dans les menus doivent toujours rester visibles » (Benoit, 21 août) : ils
    défilaient avec leur contenu et disparaissaient dès la deuxième carte. Un onglet hors
    du champ transforme la navigation en mémoire — il faut se souvenir qu'on peut changer
    d'onglet, et remonter pour le faire.

    ⚠️ Le fond opaque va sur ce CONTENEUR, jamais sur ${tick}.v-tabs${tick}. Posé sur la barre
    elle-même, il change sa couleur propre — et ${tick}probe:tabs${tick} l'a vu tout de suite : les
    quatre barres du jeu ne se ressemblaient plus, ce qui est précisément ce que Benoit
    avait demandé de corriger la veille. C'est le même motif que ${tick}.shop__head${tick}.
  */
  const head = document.createElement('div');
  head.className = 'tabbed-section__head';
  head.append(tabs.el);

  el.append(head, body);
  paint(tabs.getActive());

  return {
    el,
    select(id) {
      tabs.setActive(id);
    },
    setBadge(id, count) {
      tabs.setBadge(id, count);
    },
    getActive: () => tabs.getActive(),
  };
}
